// Tab de configuración y estado de sincronización bidireccional desktop ↔ web
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  RefreshCw, Save, Wifi, WifiOff, Clock,
  Globe, Monitor, ArrowDownToLine, ArrowUpFromLine, Bell, Zap, UserCog
} from 'lucide-react';

import { useAuth, API } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorHandler';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';

// Colecciones sincronizadas (definición del sistema, solo lectura)
const PULL_COLLECTIONS = [
  { key: 'products', label: 'Productos' },
  { key: 'categories', label: 'Categorías' },
  { key: 'tables', label: 'Mesas' },
  { key: 'users', label: 'Usuarios' },
  { key: 'role_permissions', label: 'Permisos' },
  { key: 'menu_config', label: 'Config. Menú' },
  { key: 'printers', label: 'Impresoras' },
  { key: 'taxes', label: 'Impuestos' },
  { key: 'tax_configurations', label: 'Config. Impuestos' },
  { key: 'coupons', label: 'Cupones' },
  { key: 'tenants', label: 'Tenant' },
  { key: 'ingredients', label: 'Ingredientes' },
  { key: 'recipes', label: 'Recetas' },
  { key: 'inventory', label: 'Inventario' },
  { key: 'purchases', label: 'Compras' },
];

const PUSH_COLLECTIONS = [
  { key: 'orders', label: 'Órdenes' },
  { key: 'payments', label: 'Pagos' },
  { key: 'invoices', label: 'Facturas' },
  { key: 'cash_register_sessions', label: 'Sesiones de Caja' },
  { key: 'loyalty_transactions', label: 'Trans. Fidelidad' },
  { key: 'customer_loyalty', label: 'Fidelidad Clientes' },
  { key: 'table_reservations', label: 'Reservas' },
  { key: 'accounts_receivable', label: 'Cuentas por Cobrar' },
  // Inventario y Compras son catálogo operativo, pero también suben al servidor
  // (están en SYNC_PUSH_CATALOG_COLLECTIONS): lo creado/editado en el desktop viaja a la web.
  { key: 'inventory', label: 'Inventario' },
  { key: 'purchases', label: 'Compras' },
];

 
const CollectionBadge = ({ label, count }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
    {label}
    {count != null && <span className="ml-1 text-blue-500 font-bold">{count}</span>}
  </span>
);

 
const LocalStat = ({ label, value, hint }) => (
  <div className="p-3 rounded-lg border border-emerald-200 bg-white">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 break-all">{value}</p>
    {hint && <p className="text-xs text-gray-400">{hint}</p>}
  </div>
);

const ClientCard = ({ client }) => {
  const lastPull = client.last_pull_at || client.registered_at;
  const lastPush = client.last_push_at;
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-white">
      <div className="p-2 bg-emerald-100 rounded-lg">
        <Monitor className="w-4 h-4 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">
          {client.user_email || client.instance_id?.slice(0, 8) || 'Desktop'}
        </p>
        <div className="flex gap-3 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <ArrowDownToLine className="w-3 h-3" />
            {lastPull ? new Date(lastPull).toLocaleString() : 'Nunca'}
          </span>
          <span className="flex items-center gap-1">
            <ArrowUpFromLine className="w-3 h-3" />
            {lastPush ? new Date(lastPush).toLocaleString() : 'Nunca'}
          </span>
        </div>
      </div>
      {client.last_pushed_docs != null && (
        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
          {client.last_pushed_docs} docs último push
        </span>
      )}
    </div>
  );
};
 

const SyncConfigTab = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [forcing, setForcing] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  // Motor de sync local: solo existe en modo desktop. El flag se lee de /config
  // (publico, siempre responde) y NO de /sync/status, porque ese endpoint da 404
  // cuando el motor esta apagado (sync deshabilitada o sin pareo) y eso dejaba la
  // card y el boton de cambiar usuario invisibles en un desktop perfectamente valido.
  const [desktopMode, setDesktopMode] = useState(false);
  const [localStatus, setLocalStatus] = useState(null);
  const [engineDown, setEngineDown] = useState(false);
  const [syncConfig, setSyncConfig] = useState({
    enabled: true,
    interval_minutes: 5,
  });

  const loadDashboard = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync/dashboard`, {
        headers: getAuthHeaders(),
      });
      const data = response.data;
      setDashboard(data);
      const cfg = data.sync_config || {};
      setSyncConfig({
        enabled: cfg.enabled !== false,
        interval_minutes: cfg.interval_minutes || 5,
      });
    } catch (err) {
      toast.error('Error cargando dashboard de sync', {
        description: getErrorMessage(err),
      });
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const loadLocalStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sync/status`, {
        headers: getAuthHeaders(),
      });
      setLocalStatus(response.data);
      setEngineDown(false);
    } catch (err) {
      if (err?.response?.status === 404) {
        // Desktop con el motor detenido: se muestra la card igual para que el boton
        // de cambiar usuario siga accesible (/sync/repaire no necesita el motor).
        setLocalStatus(null);
        setEngineDown(true);
        return;
      }
      console.warn('No se pudo leer el estado local de sync');
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/config`)
      .then((response) => {
        if (cancelled) return;
        const desktop = Boolean(response.data?.desktop_mode);
        setDesktopMode(desktop);
        if (desktop) loadLocalStatus();
      })
      .catch(() => {
        if (!cancelled) setDesktopMode(false);
      });
    return () => { cancelled = true; };
  }, [API, loadLocalStatus]);

  // El motor desktop trabaja en segundo plano: refrescar para ver sus ciclos periódicos
  useEffect(() => {
    if (!desktopMode) return undefined;
    const timer = setInterval(loadLocalStatus, 30000);
    return () => clearInterval(timer);
  }, [desktopMode, loadLocalStatus]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(
        `${API}/tenants/my-tenant`,
        { sync_config: syncConfig },
        { headers: getAuthHeaders() },
      );
      toast.success('Configuración guardada', {
        description: 'Los clientes desktop aplicarán los cambios en su próximo arranque o re-pairing.',
      });
    } catch (err) {
      toast.error('Error al guardar', { description: getErrorMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const handleForceSync = async () => {
    try {
      setForcing(true);
      const { data } = await axios.post(`${API}/sync/now`, {}, {
        headers: getAuthHeaders(),
      });
      if (data.status === 'ok') {
        toast.success('Sincronización manual completada', {
          description: `${data.pulled || 0} docs bajados · ${data.pushed || 0} docs subidos · ${data.files || 0} archivos`,
        });
      } else if (data.status === 'offline') {
        toast.warning('Sin conexión con el servidor', {
          description: data.message || 'El desktop sigue funcionando local y reintentará solo.',
        });
      } else {
        toast.warning('Sincronización parcial', {
          description: (data.errors || []).join(' · ') || 'Revisa el estado del motor local.',
        });
      }
      await Promise.all([loadLocalStatus(), loadDashboard()]);
    } catch (err) {
      toast.error('Error al sincronizar', { description: getErrorMessage(err) });
    } finally {
      setForcing(false);
    }
  };

  // Cambio de usuario de sync: el backend deja config.json sin tokens y relanza el
  // desktop, que vuelve a abrir el wizard /setup. Aca solo se confirma y se informa.
  const handleRepaire = async () => {
    const ok = window.confirm(
      'Vas a cambiar el USUARIO DE SINCRONIZACIÓN de este equipo.\n\n' +
      '· La app se cierra y reabre sola en la pantalla de conexión.\n' +
      '· Los datos locales NO se borran y lo pendiente de subir sigue en la cola.\n' +
      '· Si el nuevo usuario es de OTRO tenant, antes hay que borrar\n' +
      '  %APPDATA%\\SmartDineDesktop\\smartdine_desktop.db\n\n' +
      '¿Continuar?'
    );
    if (!ok) return;
    try {
      setRepairing(true);
      const { data } = await axios.post(`${API}/sync/repaire`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success('Reiniciando en modo configuración', {
        description: `${data?.message || 'Se abrira la pantalla de conexion.'} Si no abre sola, vuelve a abrir SmartDineDesktop.exe.`,
        duration: 20000,
      });
    } catch (err) {
      toast.error('No se pudo iniciar el re-pareo', { description: getErrorMessage(err) });
      setRepairing(false);
    }
  };

  const handleNotifyDesktops = async () => {
    try {
      setNotifying(true);
      await axios.post(`${API}/sync/notify-desktop`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success('Notificación enviada', {
        description: 'Los desktops sincronizarán en menos de 30 segundos en su próximo check.',
      });
      loadDashboard();
    } catch (err) {
      toast.error('Error al notificar', { description: getErrorMessage(err) });
    } finally {
      setNotifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-orange-500 mr-2" />
        <span className="text-gray-600">Cargando estado de sincronización...</span>
      </div>
    );
  }

  const clients = dashboard?.clients || [];
  const pullCounts = dashboard?.pull_collections || {};
  const pushCounts = dashboard?.push_collections || {};
  const forceSyncAt = dashboard?.force_sync_at;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header + acciones */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-xl">
            <RefreshCw className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Sincronización Bidireccional</h2>
            <p className="text-sm text-gray-500">
              Estado y configuración de la sync Web ↔ Desktop
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Solo en desktop existe motor local: boton para cobrar ya sin esperar el ciclo */}
          {desktopMode && (
            <Button
              onClick={handleForceSync}
              disabled={forcing || engineDown || localStatus?.in_flight}
              title={engineDown ? 'El motor de sincronización está detenido' : undefined}
              className="gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white"
            >
              <RefreshCw className={`w-4 h-4 ${forcing || localStatus?.in_flight ? 'animate-spin' : ''}`} />
              {forcing || localStatus?.in_flight ? 'Sincronizando...' : 'Forzar sincronización'}
            </Button>
          )}
          <Button onClick={handleNotifyDesktops} disabled={notifying} variant="outline" className="gap-2">
            <Bell className={`w-4 h-4 ${notifying ? 'animate-pulse' : ''}`} />
            {notifying ? 'Notificando...' : 'Notificar Desktops'}
          </Button>
        </div>
      </div>

      {/* Estado general */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-4 flex flex-col items-center gap-1 text-center">
          <Monitor className="w-5 h-5 text-emerald-500" />
          <p className="text-2xl font-bold text-gray-800">{clients.length}</p>
          <p className="text-xs text-gray-500">Desktops registrados</p>
        </Card>
        <Card className="p-4 flex flex-col items-center gap-1 text-center">
          <ArrowDownToLine className="w-5 h-5 text-blue-500" />
          <p className="text-2xl font-bold text-gray-800">
            {Object.values(pullCounts).reduce((a, b) => a + b, 0)}
          </p>
          <p className="text-xs text-gray-500">Docs disponibles (Web → Desktop)</p>
        </Card>
        <Card className="p-4 flex flex-col items-center gap-1 text-center">
          <ArrowUpFromLine className="w-5 h-5 text-amber-500" />
          <p className="text-2xl font-bold text-gray-800">
            {Object.values(pushCounts).reduce((a, b) => a + b, 0)}
          </p>
          <p className="text-xs text-gray-500">Docs transaccionales (Desktop → Web)</p>
        </Card>
      </div>

      {/* Motor local de este equipo (solo desktop) */}
      {desktopMode && (
        <Card className="p-5 space-y-4 border-emerald-200 bg-emerald-50/40">
          <div className="flex items-center justify-between gap-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <Zap className="w-4 h-4 text-emerald-600" />
              Motor local de este equipo
            </h4>
            <span className="text-xs text-gray-500">
              Consulta automática cada {localStatus?.sync_interval_minutes || syncConfig.interval_minutes} min
            </span>
          </div>

          {engineDown && (
            <p className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs text-orange-700">
              El motor de sincronización está detenido en este equipo (desactivado para el tenant
              o pareo incompleto), así que ahora mismo no se puede forzar. El cambio de usuario sí
              está disponible: se resuelve en el servidor local, no en el motor.
            </p>
          )}

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <LocalStat
              label="Último ciclo"
              value={localStatus?.last_cycle_at ? new Date(localStatus.last_cycle_at).toLocaleString() : 'Nunca'}
            />
            <LocalStat
              label="Bajados / Subidos"
              value={`${localStatus?.last_pulled ?? 0} ↓  ${localStatus?.last_pushed ?? 0} ↑`}
              hint={`${localStatus?.last_files_pushed ?? 0} archivos enviados`}
            />
            <LocalStat
              label="Pendientes de subir"
              value={localStatus?.pending_push_estimate ?? 0}
            />
            <LocalStat
              label="Fallos seguidos"
              value={localStatus?.consecutive_failures ?? 0}
              hint={localStatus?.remote_url}
            />
          </div>

          {localStatus?.last_error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2">
              Último error: {localStatus.last_error}
            </p>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-emerald-200">
            <p className="text-xs text-gray-600">
              Usuario de sync:{' '}
              <strong className="text-gray-800">
                {localStatus?.paired_user_email || 'no registrado (queda guardado al re-parear)'}
              </strong>
              {localStatus?.paired_tenant_id && (
                <span className="text-gray-400">
                  {' '}
                  · tenant <code className="bg-white px-1 rounded">{localStatus.paired_tenant_id.slice(0, 8)}</code>
                  {localStatus?.paired_at && <> · pareado {new Date(localStatus.paired_at).toLocaleDateString()}</>}
                </span>
              )}
            </p>
            <Button
              onClick={handleRepaire}
              disabled={repairing || forcing}
              variant="outline"
              size="sm"
              className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <UserCog className={`w-4 h-4 ${repairing ? 'animate-pulse' : ''}`} />
              {repairing ? 'Reiniciando...' : 'Cambiar usuario de sync'}
            </Button>
          </div>
        </Card>
      )}

      {/* Force sync status */}
      {forceSyncAt && (
        <Card className="p-3 bg-blue-50 border-blue-200 flex items-center gap-3 text-sm text-blue-800">
          <Bell className="w-4 h-4 shrink-0" />
          <span>
            Última notificación forzada: <strong>{new Date(forceSyncAt).toLocaleString()}</strong>
            {' '}— los desktops sincronizarán prioritariamente.
          </span>
        </Card>
      )}

      {/* Configuración */}
      <Card className="p-6 space-y-5">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Globe className="w-4 h-4 text-blue-500" />
          Parámetros
        </h3>

        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            {syncConfig.enabled ? (
              <Wifi className="w-4 h-4 text-green-600" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
            <div>
              <Label className="font-medium">Sync activada</Label>
              <p className="text-xs text-gray-500">Si se desactiva, los desktops NO sincronizarán.</p>
            </div>
          </div>
          <Switch
            checked={syncConfig.enabled}
            onCheckedChange={(val) => setSyncConfig((p) => ({ ...p, enabled: val }))}
          />
        </div>

        <div className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 bg-gray-50">
          <Clock className="w-4 h-4 text-orange-500 shrink-0" />
          <div className="flex-1">
            <Label className="font-medium">Intervalo de sincronización</Label>
            <p className="text-xs text-gray-500">Cada cuántos minutos los desktops consultan cambios.</p>
          </div>
          <Input
            type="number"
            min="1"
            max="120"
            value={syncConfig.interval_minutes}
            onChange={(e) =>
              setSyncConfig((p) => ({
                ...p,
                interval_minutes: Math.max(1, Math.min(120, parseInt(e.target.value) || 5)),
              }))
            }
            className="w-20 text-center"
          />
          <span className="text-sm text-gray-600">min</span>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            <Save className="w-4 h-4" />
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </Card>

      {/* Clientes conectados */}
      {clients.length > 0 && (
        <Card className="p-5 space-y-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-emerald-500" />
            Desktops registrados ({clients.length})
          </h4>
          <div className="space-y-2">
            {clients.map((c) => (
              <ClientCard key={c.instance_id} client={c} />
            ))}
          </div>
        </Card>
      )}

      {/* Direcciones de sync */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Web → Desktop */}
        <Card className="p-5 space-y-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <ArrowDownToLine className="w-4 h-4 text-blue-500" />
            Web → Desktop (PULL)
          </h4>
          <p className="text-xs text-gray-500">
            Catálogo y configuración: el desktop descarga cambios del servidor.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PULL_COLLECTIONS.map((col) => (
              <CollectionBadge key={col.key} label={col.label} count={pullCounts[col.key]} />
            ))}
          </div>
        </Card>

        {/* Desktop → Web */}
        <Card className="p-5 space-y-3">
          <h4 className="font-semibold text-gray-800 flex items-center gap-2">
            <ArrowUpFromLine className="w-4 h-4 text-amber-500" />
            Desktop → Web (PUSH)
          </h4>
          <p className="text-xs text-gray-500">
            Datos transaccionales y catálogo operativo (inventario, compras): el desktop sube al servidor lo generado o modificado offline.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {PUSH_COLLECTIONS.map((col) => (
              <CollectionBadge key={col.key} label={col.label} count={pushCounts[col.key]} />
            ))}
          </div>
        </Card>
      </div>

      {/* Info */}
      <Card className="p-5 bg-gray-50 border-gray-200">
        <p className="text-xs text-gray-600 leading-relaxed">
          <strong>Conflicto:</strong> Last-Write-Wins por <code>updated_at</code>.{' '}
          <strong>Idempotencia:</strong> upsert por <code>id</code>.{' '}
          <strong>Seguridad:</strong> <code>tenant_id</code> forzado desde el token JWT.{' '}
          <strong>Order numbers:</strong> el servidor reasigna en colisión.{' '}
          <strong>Notificación:</strong> {'\u00abNotificar Desktops\u00bb'} hace que el próximo ciclo PULL se ejecute en menos de 30s.{' '}
          <strong>Manual:</strong> {'\u00abForzar sincronización\u00bb'} aparece solo en el desktop y ejecuta un ciclo completo (PULL + PUSH) al instante.
        </p>
      </Card>
    </div>
  );
};

export default SyncConfigTab;
